import { db } from '@/db/db';
import { RESUME_MAX_AGE_DAYS } from '@/constants/app';
import type { Item, StudyMode, StudyRun } from './types';

/**
 * Picking up where you left off.
 *
 * A deck or a quiz saves its place after every answer, so closing the app
 * mid-run and coming back later carries on from the same question instead of
 * drawing a new deck. Device-local, like exams: where you are in a deck is a
 * property of the device you left it on.
 *
 * Two things deliberately do not resume. A run older than
 * RESUME_MAX_AGE_DAYS is dropped, because the spacing has moved on and a
 * fortnight-old deck is the wrong deck. And a run whose `nonce` differs from
 * the route's is dropped, which is how "Go again" and "New mix" ask for a
 * genuinely fresh draw.
 */

export type Want = 'flashcard' | 'question';

export interface RunKeyInput {
  want: Want;
  mode: StudyMode;
  sessionId?: string;
  category?: string;
}

/** One saved place per deck, so saved runs cannot pile up without bound. */
export function runKey({ want, mode, sessionId, category }: RunKeyInput): string {
  return `${want}|${mode}|${sessionId ?? ''}|${category ?? ''}`;
}

export function isStale(run: StudyRun, now = Date.now()): boolean {
  return now - run.updatedAt > RESUME_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
}

export interface Resumed {
  /** The saved deck, in the order it was shown, minus anything since deleted. */
  items: Item[];
  index: number;
  graded: Record<string, boolean>;
  answers: Record<string, { correct: boolean; chosenIndex?: number }>;
}

/**
 * The saved run for this deck, rebuilt against the items that exist now.
 *
 * Returns undefined when there is nothing worth resuming: no saved run, a
 * stale one, a different nonce, every item gone, or a position at or past the
 * end of what is left. A stale or unusable run is cleared on the way out, so
 * the table does not keep rows nothing will ever read.
 */
export async function loadRun(
  key: string,
  nonce: string,
  items: Item[],
  now = Date.now(),
): Promise<Resumed | undefined> {
  const run = await db.runs.get(key).catch(() => undefined);
  if (!run) return undefined;

  if (run.nonce !== nonce || isStale(run, now)) {
    await clearRun(key);
    return undefined;
  }

  const byId = new Map(items.map((i) => [i.id, i]));
  const kept = run.itemIds.map((id) => byId.get(id)).filter((i): i is Item => Boolean(i));
  // Items can vanish between runs — a re-import that replaced a session, a
  // deleted class. The position is counted in surviving items so it still
  // points at the question he was on rather than sliding backwards.
  const removedBefore = run.itemIds
    .slice(0, run.index)
    .filter((id) => !byId.has(id)).length;
  const index = run.index - removedBefore;

  if (kept.length === 0 || index >= kept.length || index < 0) {
    await clearRun(key);
    return undefined;
  }

  return {
    items: kept,
    index,
    graded: pickFor(run.graded, byId),
    answers: pickFor(run.answers, byId),
  };
}

function pickFor<T>(map: Record<string, T>, byId: Map<string, Item>): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [id, value] of Object.entries(map ?? {})) if (byId.has(id)) out[id] = value;
  return out;
}

export interface SaveRunInput extends RunKeyInput {
  nonce: string;
  items: Item[];
  index: number;
  graded?: Record<string, boolean>;
  answers?: Record<string, { correct: boolean; chosenIndex?: number }>;
  now?: number;
}

export async function saveRun(input: SaveRunInput): Promise<void> {
  const key = runKey(input);
  const now = input.now ?? Date.now();
  const existing = await db.runs.get(key).catch(() => undefined);
  const run: StudyRun = {
    id: key,
    want: input.want,
    mode: input.mode,
    sessionId: input.sessionId ?? '',
    category: input.category ?? '',
    nonce: input.nonce,
    itemIds: input.items.map((i) => i.id),
    index: input.index,
    graded: input.graded ?? {},
    answers: input.answers ?? {},
    startedAt: existing && existing.nonce === input.nonce ? existing.startedAt : now,
    updatedAt: now,
  };
  // A failed write must not take the screen down with it: the place is a
  // convenience, and the answers themselves are already recorded.
  await db.runs.put(run).catch(() => {});
}

export async function clearRun(key: string): Promise<void> {
  await db.runs.delete(key).catch(() => {});
}

/** Everything left half-finished, newest first, for the Home screen. */
export async function listRuns(now = Date.now()): Promise<StudyRun[]> {
  const rows = await db.runs.toArray().catch(() => []);
  return rows.filter((r) => !isStale(r, now)).sort((a, b) => b.updatedAt - a.updatedAt);
}
